/**
 * Translates raw pointer/keyboard/wheel input into Camera movements:
 *  - drag to pan (any mouse button; touch drag)
 *  - wheel to zoom toward the cursor
 *  - screen-edge scrolling
 *  - WASD / arrow keys for keyboard panning
 *
 * The controller owns its DOM listeners and cleans them up on `destroy()`.
 */
import type { Camera } from './Camera.js';

const EDGE_MARGIN = 24; // px from the viewport edge that triggers scrolling
const EDGE_SPEED = 900; // world px/s at zoom 1
const KEY_SPEED = 1000; // world px/s at zoom 1
const WHEEL_STEP = 1.1;

export class CameraController {
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private pointerX = 0;
  private pointerY = 0;
  private pointerInside = false;
  private readonly keys = new Set<string>();
  private edgeScrollEnabled = true;
  private dragPanEnabled = true;

  constructor(
    private readonly camera: Camera,
    private readonly element: HTMLElement,
  ) {
    element.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointermove', this.onPointerMove);
    element.addEventListener('wheel', this.onWheel, { passive: false });
    element.addEventListener('contextmenu', this.onContextMenu);
    element.addEventListener('pointerenter', this.onEnter);
    element.addEventListener('pointerleave', this.onLeave);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  setEdgeScroll(enabled: boolean): void {
    this.edgeScrollEnabled = enabled;
  }

  /** When disabled, drag does not pan (so build/spawn tools can use the drag). */
  setDragPanEnabled(enabled: boolean): void {
    this.dragPanEnabled = enabled;
    if (!enabled) this.dragging = false;
  }

  /** Current pointer position in screen space (for tile picking). */
  get pointer(): { x: number; y: number; inside: boolean } {
    return { x: this.pointerX, y: this.pointerY, inside: this.pointerInside };
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.dragPanEnabled) return;
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.element.setPointerCapture(e.pointerId);
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.dragging = false;
    if (this.element.hasPointerCapture(e.pointerId)) {
      this.element.releasePointerCapture(e.pointerId);
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    const rect = this.element.getBoundingClientRect();
    this.pointerX = e.clientX - rect.left;
    this.pointerY = e.clientY - rect.top;
    if (this.dragging) {
      this.camera.panByScreen(e.clientX - this.lastX, e.clientY - this.lastY);
      this.lastX = e.clientX;
      this.lastY = e.clientY;
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const rect = this.element.getBoundingClientRect();
    const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
    this.camera.zoomAt(e.clientX - rect.left, e.clientY - rect.top, factor);
  };

  private onContextMenu = (e: Event): void => e.preventDefault();
  private onEnter = (): void => {
    this.pointerInside = true;
  };
  private onLeave = (): void => {
    this.pointerInside = false;
  };
  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.key.toLowerCase());
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.key.toLowerCase());
  };

  /** Apply continuous (edge + keyboard) panning. Call each frame. */
  update(dtMs: number): void {
    const dt = dtMs / 1000;
    let dx = 0;
    let dy = 0;

    if (this.keys.has('a') || this.keys.has('arrowleft')) dx -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) dx += 1;
    if (this.keys.has('w') || this.keys.has('arrowup')) dy -= 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) dy += 1;
    if (dx !== 0 || dy !== 0) {
      const len = Math.hypot(dx, dy);
      this.camera.panByScreen((-dx / len) * KEY_SPEED * dt, (-dy / len) * KEY_SPEED * dt);
    }

    if (this.edgeScrollEnabled && this.pointerInside && !this.dragging) {
      let ex = 0;
      let ey = 0;
      if (this.pointerX < EDGE_MARGIN) ex = 1;
      else if (this.pointerX > this.camera.viewportW - EDGE_MARGIN) ex = -1;
      if (this.pointerY < EDGE_MARGIN) ey = 1;
      else if (this.pointerY > this.camera.viewportH - EDGE_MARGIN) ey = -1;
      if (ex !== 0 || ey !== 0) {
        this.camera.panByScreen(ex * EDGE_SPEED * dt, ey * EDGE_SPEED * dt);
      }
    }
  }

  destroy(): void {
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('wheel', this.onWheel);
    this.element.removeEventListener('contextmenu', this.onContextMenu);
    this.element.removeEventListener('pointerenter', this.onEnter);
    this.element.removeEventListener('pointerleave', this.onLeave);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }
}
